import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { AuthContext, type AuthContextValue } from './AuthContext';
import {
  activateAccountRequest,
  getCurrentUserRequest,
  loginRequest,
  registerCustomerRequest,
  resetPasswordRequest,
} from './api';
import { clearStoredToken, getStoredToken, setStoredToken } from './storage';
import type { AuthUser } from './types';

type AuthProviderProps = {
  children: ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(() => getStoredToken());
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  const storeAuthResponse = (response: { accessToken: string; user: AuthUser }) => {
    setStoredToken(response.accessToken);
    setToken(response.accessToken);
    setUser(response.user);
  };

  useEffect(() => {
    const bootstrapAuth = async () => {
      if (!token) {
        setIsBootstrapping(false);
        return;
      }

      try {
        const currentUser = await getCurrentUserRequest(token);
        setUser(currentUser);
      } catch (_error) {
        clearStoredToken();
        setToken(null);
        setUser(null);
      } finally {
        setIsBootstrapping(false);
      }
    };

    void bootstrapAuth();
  }, [token]);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: Boolean(user && token),
      isBootstrapping,
      user,
      token,
      login: async (values) => {
        const response = await loginRequest(values);
        storeAuthResponse(response);
        return response;
      },
      registerCustomer: async (values) => {
        const { accessToken, user: registeredUser, email } = await registerCustomerRequest(values);

        if (!accessToken || !registeredUser) {
          return { email, session: null };
        }

        const session = { accessToken, user: registeredUser };
        storeAuthResponse(session);
        return { email, session };
      },
      activateAccount: async (values) => {
        const response = await activateAccountRequest(values);
        storeAuthResponse(response);
        return response;
      },
      resetPassword: async (values) => {
        const response = await resetPasswordRequest(values);
        storeAuthResponse(response);
        return response;
      },
      refreshUser: async () => {
        if (!token) return null;
        const currentUser = await getCurrentUserRequest(token);
        setUser(currentUser);
        return currentUser;
      },
      logout: () => {
        clearStoredToken();
        setToken(null);
        setUser(null);
      },
    }),
    [isBootstrapping, token, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
